import { getServerSentEventsToken } from '@/api/hypertrackerApi';
import { getOrCreateGuestId } from '@/lib/helpers/guestIdHelpers';
import store from '@/store/store';
import { EventSourcePolyfill } from 'event-source-polyfill';

export type EventType =
    'message' | 'ping' | 'fileSaved' | 'buildersFee' | 'account' | 'liquidations' | 'liquidation';

class ServerEvents {
    private baseUrl: string;
    private baseUrlAuth: string;
    private eventSource: EventSource | null = null;
    private currentEndpointPath: string | null = null;
    private token: string | null = null;
    private subscriptions = new Map<EventType, (ev: MessageEvent) => void>();
    private connecting = false;
    private reconnectTimer: number | null = null;

    constructor() {
        this.baseUrl = `${import.meta.env.REACT_APP_HYPERTRACKER_URL}/guest/events`;
        this.baseUrlAuth = `${import.meta.env.REACT_APP_HYPERTRACKER_URL}/events`;
    }

    private async getSSEToken() {
        const sseTokenId = getOrCreateGuestId();
        const {
            data: { token },
        } = await getServerSentEventsToken(sseTokenId);
        this.token = token;
        return token;
    }

    private applySubscriptions(es: EventSource) {
        // @ts-ignore
        for (const [type, cb] of this.subscriptions.entries()) {
            es.addEventListener(type as any, cb);
        }
        es.addEventListener('ping', () => {
            console.log('[SSE]: ping');
        });
    }

    private getUrl(isMemberAuthorized: boolean, endpointPath?: string) {
        const baseUrl = import.meta.env.REACT_APP_HYPERTRACKER_URL;
        if (!endpointPath) return isMemberAuthorized ? this.baseUrlAuth : this.baseUrl;
        return `${baseUrl}/${endpointPath.replace(/^\/+/, '')}`;
    }

    private scheduleReconnect(isMemberAuthorized, endpointPath?: string) {
        if (this.reconnectTimer != null) return; // already scheduled
        const delay = (Math.floor(Math.random() * 4) + 1) * 1000;
        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = null;
            this.connect(isMemberAuthorized, endpointPath);
        }, delay);
    }

    async connect(isMemberAuthorized, endpointPath?: string) {
        if (this.eventSource && this.currentEndpointPath === (endpointPath ?? null)) return; // already connected
        if (this.eventSource) this.disconnect({ preserveAuth: true, preserveSubs: true });
        if (this.connecting) return; // in-flight
        this.connecting = true;
        this.currentEndpointPath = endpointPath ?? null;

        try {
            if (isMemberAuthorized) {
                const {
                    auth: {
                        tokens: { accessToken },
                    },
                } = store.getState();

                const es = new EventSourcePolyfill(this.getUrl(true, endpointPath), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                this.eventSource = es;

                es.onopen = () => {
                    this.connecting = false;
                    this.applySubscriptions(es);
                };

                es.onerror = () => {
                    this.connecting = false;
                    this.disconnect({ preserveAuth: true, preserveSubs: true });
                    this.scheduleReconnect(isMemberAuthorized, endpointPath);
                };
            } else {
                const authToken = this.token || (await this.getSSEToken());
                const es = new EventSource(
                    `${this.getUrl(false, endpointPath)}?token=${authToken}`,
                );
                this.eventSource = es;

                es.onopen = () => {
                    this.connecting = false;
                    this.applySubscriptions(es);
                };

                es.onerror = () => {
                    this.connecting = false;
                    this.disconnect({ preserveAuth: true, preserveSubs: true });
                    this.scheduleReconnect(isMemberAuthorized, endpointPath);
                };
            }
        } catch (e) {
            this.connecting = false;
            this.scheduleReconnect(isMemberAuthorized, endpointPath);
        }
    }

    disconnect(opts: { preserveAuth?: boolean; preserveSubs?: boolean } = {}) {
        if (this.reconnectTimer != null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.eventSource?.close();
        this.eventSource = null;
        this.currentEndpointPath = null;

        if (!opts.preserveAuth) {
            localStorage.removeItem('sseTokenId');
            this.token = null;
        }
        if (!opts.preserveSubs) {
            this.subscriptions.clear();
        }
    }

    subscribe(eventType: EventType, callback: (event: MessageEvent) => void) {
        const prev = this.subscriptions.get(eventType);
        if (this.eventSource && prev) {
            this.eventSource.removeEventListener(eventType as any, prev);
        }
        this.subscriptions.set(eventType, callback);
        if (this.eventSource) {
            this.eventSource.addEventListener(eventType as any, callback);
        }
    }

    unsubscribe(eventType: EventType) {
        const prev = this.subscriptions.get(eventType);
        if (prev && this.eventSource) {
            this.eventSource.removeEventListener(eventType as any, prev);
        }
        this.subscriptions.delete(eventType);
    }
}

const instance = new ServerEvents();
export default instance;
